import { useCallback, useRef, useState } from 'react'
import { useLanguage } from '@context'
import { isNetworkError, showErrorToast } from '@utils'
import { type ScanCheckInResult, useRegisterCheckIn, useScanCheckIn } from '@hooks'
import { useOfflineCheckIn } from '../offlineCheckIn/useOfflineCheckIn'

/**
 * Shared scan/register state machine: native and web only differ in how they
 * turn a camera frame into a string, everything after `handleCode` is common.
 * Offline: a scan that can't reach the API falls back to the manifest
 * downloaded when the entrance screen opened, and queues the check-in for
 * later sync — "L'inscrire" has no offline path (it needs the server to
 * create the seat).
 */
export function useCheckInScanController(eventId: string) {
  const { t } = useLanguage()
  const scan = useScanCheckIn(eventId)
  const register = useRegisterCheckIn(eventId)
  const offline = useOfflineCheckIn(eventId)
  const [result, setResult] = useState<ScanCheckInResult | null>(null)
  const resultRef = useRef<ScanCheckInResult | null>(null)
  resultRef.current = result

  const handleCode = useCallback(
    (code: string) => {
      // A result is already showing, or a scan is in flight: ignore extra frames.
      if (resultRef.current || scan.isPending) return
      scan.mutate(code, {
        onSuccess: (online) => {
          offline.applyOnlineResult(online)
          setResult(online)
        },
        onError: (error) => {
          if (!isNetworkError(error)) return // the hook's own onError already toasted it
          setResult(offline.recordOfflineScan(code))
        },
      })
    },
    [scan, offline],
  )

  const continueScanning = useCallback(() => setResult(null), [])

  const onRegister = useCallback(() => {
    if (resultRef.current?.status !== 'not_registered') return
    register.mutate(resultRef.current.accountId, {
      onSuccess: (registered) => {
        if (registered.status === 'checked_in' || registered.status === 'already_checked_in') {
          offline.applyOnlineResult(registered)
          setResult(registered)
          return
        }
        const key =
          registered.status === 'full'
            ? 'registerFull'
            : registered.status === 'closed'
              ? 'registerClosed'
              : registered.status === 'payment_required'
                ? 'registerPaymentRequired'
                : 'registerInvalid'
        showErrorToast(t(`events.checkin.scan.${key}`))
      },
    })
  }, [register, t, offline])

  return {
    result,
    isResultShowing: result !== null,
    handleCode,
    continueScanning,
    onRegister,
    isScanning: scan.isPending,
    isRegistering: register.isPending,
    isOnline: offline.isOnline,
    pendingCount: offline.pendingCount,
  }
}
